import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    Injector,
    input,
} from '@angular/core';
import {takeUntilDestroyed, toObservable, toSignal} from '@angular/core/rxjs-interop';
import {ReactiveFormsModule} from '@angular/forms';
import {map, merge, of, switchMap} from 'rxjs';
import {FeatureFlagService, TranslatePipe} from '@brunata-angular/util';
import {InputDateGroupComponent, InputEmailListComponent} from '@brunata-angular/ui';
import {
    deliveryChannelLabelKeys,
    maxSavedExportRecipients,
    recurrenceLabelKeys,
    savedExportDeliveryChannels,
    SavedExportDeliveryChannel,
    savedExportRecurrences,
} from '../../saved-exports/saved-export.model';
import {
    conditionalScheduleControls,
    ExportScheduleForm,
    scheduleFormValue,
} from '../../shared/models/export-form.model';
import {injectHoldsAdvancedDelivery} from '../../shared/services/advanced-delivery';
import {CustomScheduleComponent} from './custom-schedule.component';
import {SftpDeliveryPanelComponent} from './sftp-delivery-panel.component';
import {StepCardDirective} from '../step-card/step-card.directive';
import {stepLabels} from '../step-card/step-labels.model';

const recurrenceOptions = savedExportRecurrences.map(option => ({
    value: option,
    labelKey: recurrenceLabelKeys[option],
}));

const channelTabs = savedExportDeliveryChannels.map(channel => ({
    value: channel,
    labelKey: deliveryChannelLabelKeys[channel],
}));

@Component({
    selector: 'app-schedule-step',
    standalone: true,
    providers: [
        stepLabels(
            'customizedexports_export_step_schedule_title',
            'customizedexports_export_step_schedule_hint',
        ),
    ],
    hostDirectives: [{directive: StepCardDirective, inputs: ['titleKey', 'hintKey']}],
    imports: [
        ReactiveFormsModule,
        TranslatePipe,
        InputDateGroupComponent,
        InputEmailListComponent,
        CustomScheduleComponent,
        SftpDeliveryPanelComponent,
    ],
    template: `
        <div class="schedule-flow border">
            @if (sftpDeliveryOffered()) {
                <ul class="nav nav-tabs" role="tablist">
                    @for (tab of channelTabs; track tab.value) {
                        <li class="nav-item" role="presentation">
                            <button
                                type="button"
                                class="nav-link"
                                role="tab"
                                [class.active]="channel() === tab.value"
                                [attr.aria-selected]="channel() === tab.value"
                                (click)="selectChannel(tab.value)">
                                {{ tab.labelKey | translate: 'online' : 'export' }}
                            </button>
                        </li>
                    }
                </ul>
            }

            <div class="p-3">
                @if (channel() === 'SFTP') {
                    @if (holdsAdvancedDelivery() === true) {
                        <app-sftp-delivery-panel
                            [schedule]="schedule()"
                            [fileName]="sftpFileName()" />
                    } @else if (holdsAdvancedDelivery() === false) {
                        <div class="alert alert-web-blue d-flex align-items-start gap-2 mb-0">
                            <i class="bi bi-info-circle-fill flex-shrink-0" aria-hidden="true"></i>
                            <div>
                                {{
                                    'savedexport_sftp_requires_advanced_delivery'
                                        | translate: 'online' : 'export'
                                }}
                            </div>
                        </div>
                    }
                }
                <div [hidden]="channel() === 'SFTP'">
                    <div class="alert alert-web-blue d-flex align-items-start gap-2 mb-3">
                        <i class="bi bi-info-circle-fill flex-shrink-0" aria-hidden="true"></i>
                        <div>
                            {{ 'savedexport_email_subject_info' | translate: 'online' : 'export' }}
                        </div>
                    </div>
                    <app-input-email-list
                        [formControl]="schedule().controls.recipients"
                        [required]="channel() !== 'SFTP'"
                        [label]="'savedexport_recipients_label' | translate: 'online' : 'export'"
                        [maxItems]="maxRecipients"
                        [maxItemsLabel]="
                            'savedexport_recipients_max_reached'
                                | translate: 'online' : 'export' : [maxRecipients.toString()]
                        " />
                </div>
            </div>

            <div class="bg-primary-faint border-top p-3">
                <div class="d-flex flex-wrap row-gap-3 column-gap-5">
                    <fieldset
                        class="border-0 p-0 m-0"
                        style="flex: 1 1 15rem"
                        role="radiogroup"
                        [disabled]="customSchedule()"
                        [class.opacity-50]="customSchedule()"
                        [attr.aria-label]="
                            'savedexport_recurrence_label' | translate: 'online' : 'export'
                        ">
                        <div class="form-label">
                            {{ 'savedexport_recurrence_label' | translate: 'online' : 'export' }}
                        </div>
                        <div class="d-flex flex-column gap-3">
                            @for (option of recurrenceOptions; track option.value) {
                                <div class="form-check">
                                    <input
                                        type="radio"
                                        class="form-check-input"
                                        [formControl]="schedule().controls.recurrence"
                                        [value]="option.value"
                                        [id]="'recurrence-' + option.value" />
                                    <label
                                        class="form-check-label"
                                        [for]="'recurrence-' + option.value">
                                        {{ option.labelKey | translate: 'online' : 'export' }}
                                    </label>
                                </div>
                            }
                        </div>
                    </fieldset>

                    <div style="flex: 1 1 15rem">
                        <app-input-date-group
                            [formControl]="schedule().controls.startDate"
                            [required]="true"
                            [noBottomMargin]="true"
                            [label]="
                                'savedexport_start_date_label' | translate: 'online' : 'export'
                            " />
                        <div class="form-text mt-2">
                            {{ 'savedexport_start_date_hint' | translate: 'online' : 'export' }}
                        </div>
                    </div>
                </div>

                @if (customScheduleOffered()) {
                    <app-custom-schedule
                        class="d-block mt-3"
                        [schedule]="schedule()"
                        [daysBackInTime]="daysBackInTime()"
                        [latestKnownReadings]="latestKnownReadings()" />
                }
            </div>
        </div>
    `,
    styles: `
        .schedule-flow {
            --bs-border-color: rgba(var(--bs-primary-rgb), 0.15);
        }

        .nav-tabs .nav-link {
            border-width: 0 0 3px;
            border-bottom-color: transparent;
        }

        .nav-tabs .nav-link.active {
            border-bottom-color: var(--bs-primary);
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScheduleStepComponent {
    readonly schedule = input.required<ExportScheduleForm>();
    readonly customizedSection = input(false);
    readonly daysBackInTime = input<number | null>(null);
    readonly latestKnownReadings = input(false);
    readonly sftpFileName = input<string | null>(null);
    private readonly injector = inject(Injector);

    private readonly featureFlagService = inject(FeatureFlagService);

    protected readonly sftpEnabled = toSignal(
        this.featureFlagService.isFeatureFlagEnabled$('online-sftp-connections'),
        {initialValue: false},
    );

    protected readonly holdsAdvancedDelivery = injectHoldsAdvancedDelivery();

    private readonly value = scheduleFormValue(this.schedule);

    protected readonly channel = computed<SavedExportDeliveryChannel>(
        () => this.value()?.deliveryChannel ?? 'EMAIL',
    );

    protected readonly customSchedule = computed(() => this.value()?.customSchedule ?? false);

    protected readonly sftpDeliveryOffered = computed(
        () =>
            this.customizedSection() &&
            ((this.sftpEnabled() && this.holdsAdvancedDelivery() === true) ||
                this.channel() === 'SFTP'),
    );

    protected readonly customScheduleOffered = computed(
        () => this.customizedSection() && (this.sftpEnabled() || this.customSchedule()),
    );

    protected readonly recurrenceOptions = recurrenceOptions;
    protected readonly channelTabs = channelTabs;
    protected readonly maxRecipients = maxSavedExportRecipients;

    constructor() {
        // Recipients and the connection are each required only on their own tab, and the unit and
        // period decide which custom-schedule fields matter, so every switch revalidates them.
        toObservable(this.schedule)
            .pipe(
                switchMap(({controls}) =>
                    merge(
                        merge(
                            of(undefined),
                            controls.customSchedule.valueChanges,
                            controls.scheduleUnit.valueChanges,
                            controls.schedulePeriod.valueChanges,
                        ).pipe(map(() => ({controls, afterRender: false}))),
                        controls.deliveryChannel.valueChanges.pipe(
                            map(() => ({controls, afterRender: true})),
                        ),
                    ),
                ),
                takeUntilDestroyed(),
            )
            .subscribe(({controls, afterRender}) => {
                const revalidate = (): void =>
                    conditionalScheduleControls.forEach(name =>
                        controls[name].updateValueAndValidity(),
                    );
                if (afterRender) {
                    afterNextRender(revalidate, {injector: this.injector});
                } else {
                    revalidate();
                }
            });
    }

    protected selectChannel(channel: SavedExportDeliveryChannel): void {
        this.schedule().controls.deliveryChannel.setValue(channel);
        this.schedule().controls.deliveryChannel.markAsDirty();
    }
}
